import React, { useState, useEffect } from 'react';

import { Grid, Modal } from 'semantic-ui-react';
import { CloseDark } from '../../icons/icons';
import Loader from '../../components/loader';
import '../styles/TemplateListModal.scss';
import SearchInput from '../../components/SearchInput/SearchInput';
import { useActions } from '../../hooks/useActions';
import { getSasToken } from '../../actions';
import { RootState, SasToken } from 'gf-types';
import { useAppSelector } from '../../hooks/ReduxHooks';
import { getTemplateFilterParams } from '../helpers';
import { GoFormzServices } from '../../AppSettings';
import { useApim } from '../../hooks/useApi';

enum EntityType {
  Template = 2,
  Folder = 3
}

export interface TemplateListModalProps {
  onClose?: AnyFunc;
  selectTemplate?: AnyFunc;
}

export default function TemplateListModal(props: TemplateListModalProps) {
  const { onClose, selectTemplate } = props;

  const [search, setSearch] = useState('');
  const [folderId, setFolderId] = useState(null);
  const [folderName, setFolderName] = useState(null);
  const [subFolders, setSubFolders] = useState(false);

  const [getSasTokenAction] = useActions([getSasToken]);
  const sasToken = useAppSelector<SasToken>((state: RootState) => state.shared.sasToken);
  const params = getTemplateFilterParams(true, 10000, 1, search, folderId, true, false, subFolders);
  const [templatesResult, templatesLoading] = useApim<TypeMePlease>(GoFormzServices.TemplateService, `/templates?${params.toString()}`);

  useEffect(() => {
    if (folderId) {
      setSubFolders(true);
    } else {
      setSubFolders(false);
    }
  }, [folderId]);

  useEffect(() => {
    getSasTokenAction();
  }, []);

  const selectItem = (item) => {
    if (item.entityType === EntityType.Template) {
      onClose();
      selectTemplate(item.id);
    } else {
      setFolderName(item.name);
      setFolderId(item.id);
    }
  };

  const baseUrl = sasToken?.baseUrl || '';
  const token = sasToken?.token || '';

  const thumbnail = (item) => {
    if (item.entityType === EntityType.Template) {
      if (!item.thumbnailFileId) {
        return <div className="thumb nobackground" />;
      }
      return <img className="thumb template" src={`${baseUrl}${item.thumbnailFileId}${token}`} alt="" />;
    }
    return <div className="thumb folder" />;
  };

  let cells = null;
  if (!templatesLoading && templatesResult && templatesResult.items) {
    // First, gather all folder IDs present in the results
    const presentFolderIds = templatesResult.items.filter((item) => item.entityType === EntityType.Folder).map((folder) => folder.id);

    // Filter items: show all templates and only show subfolders if their parent is not present
    const filteredItems = templatesResult.items.filter((item) => {
      // Always show templates
      if (item.entityType === EntityType.Template) {
        return true;
      }

      // If it's a folder with no parentId (top-level folder), always show it
      if (item.entityType === EntityType.Folder && !item.parentId) {
        return true;
      }

      // If it's a subfolder, only show it if its parent folder is not present in the results
      if (item.entityType === EntityType.Folder && item.parentId) {
        return !presentFolderIds.includes(item.parentId);
      }

      return true;
    });

    cells = filteredItems.map((item) => {
      return (
        <Grid.Column
          key={item.id}
          onClick={() => {
            selectItem(item);
          }}
        >
          {thumbnail(item)}
          <span>{item.name}</span>
        </Grid.Column>
      );
    });
  }

  const body = (
    <div>
      <div className="toolbar">
        <div className="breadcrumb">
          <span className="folder-label">FOLDER</span>
          {folderName ? (
            <React.Fragment>
              <button className="breadcrumb-item breadcrumb-main link-button" onClick={() => setFolderId(null)}>
                MAIN
              </button>
              <span className="breadcrumb-item breadcrumb-folder">{folderName}</span>
            </React.Fragment>
          ) : (
            <span className="breadcrumb-item breadcrumb-main">MAIN</span>
          )}
        </div>
        <div className="search-container">
          {search ? (
            <button className="link-button mobile-hidden" onClick={() => setSearch('')}>
              Clear search
            </button>
          ) : null}
          <SearchInput placeholder="Search templates" searchFilter={search} onSearchChange={setSearch} />
        </div>
      </div>
      <div className="grid-container">
        {templatesLoading ? <Loader isLoading /> : null}
        {(!cells || cells.length === 0) && !templatesLoading ? <div className="empty-state">{search ? 'No matching results' : 'Folder is empty'}</div> : null}
        <Grid container>{cells}</Grid>
      </div>
    </div>
  );

  return (
    <Modal open onClose={onClose} closeOnDimmerClick={false} dimmer="inverted" size="large" className="templatelist-modal">
      <Modal.Header>
        Select a form template
        <button className="close-button plain-button" tabIndex={-1} onClick={onClose}>
          <CloseDark />
        </button>
      </Modal.Header>
      <Modal.Content>{body}</Modal.Content>
    </Modal>
  );
}
